<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import StatusTag from 'dashboard/components-next/tag/StatusTag.vue';
import { STAGE_META, resolveLeadStage } from '../utils/crmPageUtils';
import {
  isPfaVisitor,
  proposalsInProgressQuantity,
  visitorAttendanceStage,
  visitorAttendanceStageLabel,
  visitorClientSinceLabel,
  visitorName,
  visitorPhone,
  visitorProfileTags,
  visitsQuantity,
} from '../utils/visitorInfoPresenter';
import LeadAvatar from './LeadAvatar.vue';

const props = defineProps({
  visitorInfo: { type: Object, default: null },
  lead: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['phone', 'chat']);

const HEADER_COPY = Object.freeze({
  anonymous: 'Visitante',
  clientSince: 'Cliente desde',
  clientSinceEmpty: 'Data de entrada não informada',
  pfaClient: 'Cliente PFA',
  phone: 'Ligar',
  chat: 'Abrir conversa',
  phoneUnavailable: 'Telefone não informado',
});

const HEADER_STAGE_LABELS = Object.freeze({
  sem_visita: 'Sem visita',
  uma_ou_duas_visitas: '1 ou 2 visitas',
  tres_ou_mais_visitas: '3 ou mais visitas',
  proposta_em_andamento: 'Proposta em andamento',
  negocio_fechado: 'Negócio fechado',
});

const name = computed(
  () => visitorName(props.visitorInfo) || HEADER_COPY.anonymous
);
const phone = computed(() => visitorPhone(props.visitorInfo));
const clientSince = computed(() => visitorClientSinceLabel(props.visitorInfo));
const isPfa = computed(() => isPfaVisitor(props.visitorInfo, props.lead || {}));
const fallbackLead = computed(() => ({
  name: name.value,
  client_type: isPfa.value ? 'PFA' : '',
  attendance_stage: visitorAttendanceStage(props.visitorInfo),
  finished_visits_count: visitsQuantity(props.visitorInfo),
  active_offers_count: proposalsInProgressQuantity(props.visitorInfo),
}));
const avatarLead = computed(() => ({
  ...(props.lead || {}),
  ...fallbackLead.value,
  additional_attributes: {
    ...(props.lead?.additional_attributes || {}),
    client_type: isPfa.value
      ? 'PFA'
      : props.lead?.additional_attributes?.client_type,
  },
}));
const stageKey = computed(
  () =>
    visitorAttendanceStage(props.visitorInfo) ||
    resolveLeadStage(props.lead || fallbackLead.value)
);
const stageMeta = computed(
  () => STAGE_META[stageKey.value] || STAGE_META.sem_visita
);
const stageLabel = computed(
  () =>
    visitorAttendanceStageLabel(props.visitorInfo) ||
    props.lead?.attendance_stage_label ||
    HEADER_STAGE_LABELS[stageKey.value] ||
    HEADER_STAGE_LABELS.sem_visita
);
const visibleTags = computed(() => {
  const blockedLabels = new Set([
    stageLabel.value,
    HEADER_COPY.pfaClient,
    'PFA',
    '',
  ]);

  return visitorProfileTags(props.visitorInfo, props.lead || {})
    .filter(tag => !blockedLabels.has(tag))
    .slice(0, 6);
});
const phoneLabel = computed(() =>
  phone.value
    ? `${HEADER_COPY.phone}: ${phone.value}`
    : HEADER_COPY.phoneUnavailable
);

const emitPhone = () =>
  emit('phone', {
    participantUserId: props.visitorInfo?.visitor_id,
    isPhoneDialer: true,
  });

const emitChat = () =>
  emit('chat', {
    participantUserId: props.visitorInfo?.visitor_id,
  });
</script>

<template>
  <section
    class="overflow-hidden rounded-lais border border-lais-border-soft bg-white"
  >
    <div
      v-if="loading"
      class="grid gap-4 px-4 py-4 sm:grid-cols-[1fr_auto] sm:items-center"
    >
      <div class="flex min-w-0 items-center gap-3">
        <span class="size-12 animate-pulse rounded-full bg-lais-bg-muted" />
        <span class="grid flex-1 gap-2">
          <span
            class="h-5 w-52 max-w-full animate-pulse rounded bg-lais-bg-muted"
          />
          <span
            class="h-4 w-36 max-w-full animate-pulse rounded bg-lais-bg-muted"
          />
        </span>
      </div>
      <span class="h-10 w-24 animate-pulse rounded-full bg-lais-bg-muted" />
    </div>

    <div
      v-else
      class="grid gap-4 px-4 py-4 sm:grid-cols-[1fr_auto] sm:items-center"
    >
      <div class="flex min-w-0 items-start gap-3">
        <LeadAvatar :lead="avatarLead" size-class="size-12" />
        <div class="min-w-0 flex-1">
          <h2
            class="truncate font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
          >
            {{ name }}
          </h2>
          <p class="mt-1 truncate text-lais-body-sm text-lais-text-muted">
            {{
              clientSince
                ? `${HEADER_COPY.clientSince} ${clientSince}`
                : HEADER_COPY.clientSinceEmpty
            }}
          </p>
          <div class="mt-3 flex flex-wrap gap-2">
            <StatusTag
              :label="stageLabel"
              :variant="stageMeta.variant"
              size="xs"
            />
            <StatusTag
              v-if="isPfa"
              :label="HEADER_COPY.pfaClient"
              variant="attention"
              size="xs"
            />
            <span
              v-for="tag in visibleTags"
              :key="tag"
              class="max-w-full truncate rounded-full border border-lais-border-soft bg-lais-bg-soft px-2 py-0.5 text-[11px] font-semibold text-lais-text-muted"
              :title="tag"
            >
              {{ tag }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex shrink-0 items-center gap-2">
        <button
          type="button"
          class="grid size-10 place-items-center rounded-full border border-lais-border-soft text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!phone"
          :aria-label="phoneLabel"
          :title="phoneLabel"
          @click="emitPhone"
        >
          <span class="i-lucide-phone size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="grid size-10 place-items-center rounded-full bg-lais-primary text-white transition hover:bg-lais-primary-600"
          :aria-label="HEADER_COPY.chat"
          :title="HEADER_COPY.chat"
          @click="emitChat"
        >
          <span class="i-lucide-message-circle size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  </section>
</template>
